import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@store/store';
import { DeliveryTypeDef } from '@api/types/DeliveryTypeDef';
import { PaymentTypeDef } from '@api/types/PaymentTypeDef';
import { DispatchPoint } from '@api/types/DispatchPoint';
import { OrderedDogsUnits, OrderedCatsUnits, OrderedCatsCollarUnits } from '@store/partners-order/OrderedUnits';
import { invalidFormValue, isValidEmail, isValidPhoneNumber, isSpecificDelivery } from '@components/utils';
import { AppLanguageCode } from '@project-types/AppLanguageCodes';



export const selectPartnersOrderState = (state: RootState) => state.partnersOrder;
export const selectShowInvalids = (state: RootState) => state.partnersOrder.showInvalids;

export const selectCustomer = (state: RootState) => state.partnersOrder.customer;
export const selectEmail = (state: RootState) => state.partnersOrder.email;
export const selectPhoneNumber = (state: RootState) => state.partnersOrder.phoneNumber;

export const selectStreet = (state: RootState) => state.partnersOrder.street;
export const selectCity = (state: RootState) => state.partnersOrder.city;
export const selectPostalCode = (state: RootState) => state.partnersOrder.postalCode;
export const selectInvoiceStreet = (state: RootState) => state.partnersOrder.invoiceStreet;
export const selectInvoiceCity = (state: RootState) => state.partnersOrder.invoiceCity;
export const selectInvoicePostalCode = (state: RootState) => state.partnersOrder.invoicePostalCode;

export const selectRegistrationNumber = (state: RootState) => state.partnersOrder.registrationNumber;
export const selectVatnumber = (state: RootState) => state.partnersOrder.vatnumber;
export const selectMessage = (state: RootState) => state.partnersOrder.message;
export const selectPayment = (state: RootState) => state.partnersOrder.payment;
export const selectDelivery = (state: RootState) => state.partnersOrder.delivery;
export const selectDispatchPoint = (state: RootState) => state.partnersOrder.dispatchPoint;
export const selectOrderedDogsUnits = (state: RootState) => state.partnersOrder.orderedDogsUnits;
export const selectOrderedCatsUnits = (state: RootState) => state.partnersOrder.orderedCatsUnits;
export const selectOrderedCatsCollarUnits = (state: RootState) => state.partnersOrder.orderedCatsCollarUnits;
export const selectTestSamples = (state: RootState) => state.partnersOrder.testSamples;
export const selectCountry = (state: RootState) => state.partnersOrder.country;



export const selectOrderedUnitsIsEmpty = createSelector(
	[
		selectOrderedDogsUnits,
		selectOrderedCatsUnits,
		selectOrderedCatsCollarUnits,
		selectTestSamples,
	],(
		orderedDogsUnits: OrderedDogsUnits,
		orderedCatsUnits: OrderedCatsUnits,
		orderedCatsCollarUnits: OrderedCatsCollarUnits,
		testSamples: boolean,
	) => {
		let allDogValuesZero = Object.values(orderedDogsUnits).every(x => x === 0);
		let allCatValuesZero = Object.values(orderedCatsUnits).every(x => x === 0);
		let allCatCollarValuesZero = Object.values(orderedCatsCollarUnits).every(x => x === 0);
		if (allDogValuesZero && allCatValuesZero && allCatCollarValuesZero && !testSamples) return true;
		return false;
});



export const selectIsValid = createSelector(
	[
		selectCustomer,
		selectEmail,
		selectPhoneNumber,
		selectRegistrationNumber,
		selectVatnumber,
		selectOrderedUnitsIsEmpty,
		selectPayment,
		selectDelivery,
		selectDispatchPoint,
		selectCountry,
	],(
		customer: string | null,
		email: string | null,
		phoneNumber: string | null,
		registrationNumber: string | null,
		vatnumber: string | null,
		orderedUnitsIsEmpty: boolean,
		payment: PaymentTypeDef | null,
		delivery: DeliveryTypeDef | null,
		dispatchPoint: DispatchPoint | null,
		country: AppLanguageCode,
	) => {
		if (invalidFormValue(customer)) return false;
		if (process.env.SHOW_COMPANY_REGISTRATION_ID === 'TRUE' && invalidFormValue(registrationNumber)) return false;
		if (invalidFormValue(vatnumber)) return false;
		if (invalidFormValue(payment)) return false;
		if (invalidFormValue(delivery)) return false;
		if (invalidFormValue(country)) return false;
		if (email && email.length > 0 && !isValidEmail(email)) return false;
		if (phoneNumber && phoneNumber.length > 0 && !isValidPhoneNumber(phoneNumber)) return false;
		if (orderedUnitsIsEmpty) return false;
		
		if (delivery && isSpecificDelivery(delivery.id, process.env.ZASILKOVNA_ID)) {
			if (invalidFormValue(dispatchPoint)) return false;
		}
		if (delivery && isSpecificDelivery(delivery.id, process.env.DPD_ID)) {
			if (invalidFormValue(dispatchPoint)) return false;
		}
		if (delivery && isSpecificDelivery(delivery.id, process.env.PPL_ID)) {
			if (invalidFormValue(dispatchPoint)) return false;
		}
		return true;
});